import { BehaviorSubject } from "rxjs";
import { FillExtrusionLayerSpecification, FillLayerSpecification, Map as MapboxMap } from "@iventis/mapbox-gl";
import { Polygon, Feature } from "geojson";
import { MapObjectProperties } from "@iventis/map-types";
import { createStaticStyleValue } from "@iventis/layer-style-helpers/src/create-style-value-helpers";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import { lineString } from "@turf/helpers";
import { MapModuleLayer, MapState } from "../../types/store-schema";
import { Layer } from "../generic-layers/layer";
import { styleValueToMapboxStyleValue } from "../mapbox/engine-mapbox-helpers";
import { StylePropertyToValueMapV2 } from "../../types/style-types";
import { IMapboxLayerManager, MapboxLayerManager } from "./mapbox-layer-manager";
import { transparentColour } from "../mapbox/engine-mapbox-types-and-constants";
import { MapboxHighlightLayer } from "./mapbox-highlight-layer";
import { MapboxHighlightLineLayer } from "./mapbox-highlight-line-layer";
import { MapboxLineBaseLayer } from "./mapbox-line-base-layer";
import { outlineSuffix } from "../constants";

type AreaSource = Feature<Polygon, MapObjectProperties>[];

type SupportedAreaMapboxLayers = FillLayerSpecification | FillExtrusionLayerSpecification;

const getAreaBaseOutlineLayerId = (layerId: string) => `${layerId}_${outlineSuffix}`;

/** Base class for any area-based layer */
export class MapboxAreaBaseLayer extends Layer<AreaSource, Partial<AreaStyle>> {
    public readonly layerId: string;

    protected readonly sourceId: string;

    private readonly mapboxLayerManager: IMapboxLayerManager<AreaSource>;

    private readonly highlightsLayer: MapboxHighlightLayer;

    public readonly outlineLayer: MapboxLineBaseLayer;

    public get hasLocalLayer() {
        return this.mapboxLayerManager.hasLocalLayer;
    }

    public get hasRemoteLayer() {
        return this.mapboxLayerManager.hasRemoteLayer;
    }

    constructor(
        map: MapboxMap,
        layer: MapModuleLayer,
        state$: BehaviorSubject<MapState>,
        private readonly accessors: {
            getAboveLayerId: () => string;
            getStyle: (layer: MapModuleLayer) => Partial<AreaStyle>;
        }
    ) {
        super(state$);
        this.layerId = layer.id;
        this.sourceId = layer.id;
        this.mapboxLayerManager = new MapboxLayerManager(map, this.layerId);
        this.highlightsLayer = new MapboxHighlightLineLayer(this.layerId, map);
        const outlineLayerId = getAreaBaseOutlineLayerId(this.layerId);
        this.outlineLayer = new MapboxLineBaseLayer(
            map,
            { ...layer, id: outlineLayerId, source: outlineLayerId },
            state$,
            { name: layer.name, type: "outline" },
            {
                getAboveLayerId: () => this.layerId,
                // For now, the width can just be static. In future it would be derived from the area style
                getStyle: (layer) => ({ colour: this.accessors.getStyle(layer).colour, width: createStaticStyleValue(2) }),
            }
        );
    }

    public addLayer() {
        if (!this.mapboxLayerManager.hasLocalLayer) {
            this.mapboxLayerManager.createLocalLayerClass();
        }
        this.mapboxLayerManager.addLocalSource();
        const layer = this.getIventisLayer();
        const mapboxLayer = this.iventisLayerToMapboxLayer(layer);
        this.mapboxLayerManager.addLayerToMapbox(mapboxLayer, { layerSourceType: "local", aboveLayerId: this.accessors.getAboveLayerId() });
        this.updateMapLevel(this.state$.value.currentLevel);
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.mapboxLayerManager.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
        }
    }

    public removeLayer() {
        this.mapboxLayerManager.removeLayer();
        this.outlineLayer.removeLayer();
        this.removeHighlights();
    }

    public addLocalSource() {
        this.mapboxLayerManager.addLocalSource();
        this.highlightsLayer.addSource();
    }

    public updateLocalSource(source: AreaSource): void {
        if (!this.mapboxLayerManager.hasLocalLayer) {
            this.addLayer();
        }
        this.mapboxLayerManager.updateLocalSource(source);
        this.highlightsLayer.upsertSource(source.map((feature) => lineString(feature.geometry.coordinates[0], feature.properties)));
    }

    public removeLocalSource(): void {
        this.mapboxLayerManager.removeLocalSource();
    }

    public hideLayer(): void {
        this.mapboxLayerManager.hideLayer();
    }

    public showLayer(): void {
        this.mapboxLayerManager.showLayer();
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.mapboxLayerManager.updateMapOrder(aboveLayerId);
        this.outlineLayer.updateMapOrder(this.layerId);
        this.highlightsLayer.updateMapOrder(this.outlineLayer.layerId);
    }

    public updateMapLevel(level: number) {
        this.mapboxLayerManager.updateMapLevel(level);
    }

    public addDateFilter() {
        const layer = this.getIventisLayer();
        const dateFilter = this.state$.value.datesFilter.value;
        if (dateFilter.filter) {
            this.mapboxLayerManager.addDateFilter(layer.dataFields, dateFilter.day, dateFilter.time);
        }
    }

    public removeDateFilter() {
        this.mapboxLayerManager.removeDateFilter();
    }

    public async updateStyle(styleChanges: StylePropertyToValueMapV2<AreaStyle>) {
        if (styleChanges.colour) {
            this.mapboxLayerManager.setPaintProperty("fill-color", styleValueToMapboxStyleValue(styleChanges.colour));
        }
        if (styleChanges.opacity) {
            this.mapboxLayerManager.setPaintProperty("fill-opacity", styleValueToMapboxStyleValue(styleChanges.opacity));
        }
        // Only update the outline style if it is in use
        if (this.outlineLayer.hasLocalLayer || this.outlineLayer.hasRemoteLayer) {
            this.outlineLayer.updateStyle(styleChanges);
        }
    }

    protected iventisLayerToMapboxLayer(iventisLayer: MapModuleLayer): SupportedAreaMapboxLayers {
        const style = this.accessors.getStyle(iventisLayer);
        const layer: FillLayerSpecification = {
            id: this.layerId,
            type: "fill",
            paint: {
                "fill-color": styleValueToMapboxStyleValue(style.colour),
                "fill-opacity": styleValueToMapboxStyleValue(style.opacity) ?? 1,
                "fill-outline-color": transparentColour,
            },
            layout: {
                visibility: iventisLayer.visible ? "visible" : "none",
                "fill-sort-key": styleValueToMapboxStyleValue(style.objectOrder ?? createStaticStyleValue(0)),
            },
            source: iventisLayer.source,
            metadata: {
                name: iventisLayer.name,
                type: "base",
            },
        };
        return layer;
    }

    public highlightObjects(objectIds: string[]): void {
        const geojson = this.getGeoJson();
        if (geojson == null) {
            return;
        }
        this.highlightsLayer.addLayer(this.getIventisLayer(), this.mapboxLayerManager.localLayerId);
        this.highlightsLayer.highlightObjects(objectIds);
    }

    public removeHighlights(): void {
        this.highlightsLayer.highlightObjects([]);
    }

    public getLayerIdsForSnapping(): string[] {
        return this.mapboxLayerManager.getActiveLayerIds();
    }

    public onDrawingStarted(objectIds: string[]): void {
        super.onDrawingStarted(objectIds);
        this.removeHighlights();
    }
}
